/** Private compiled-score transport. Authoring and generation use storyActions exclusively. */
import { z } from "zod";
import { zStoryInteraction } from "./storyInteraction";
import { zCameraZoom, zCameraZoomCenter, zStoryAspectRatio } from "./storyFraming";
import { zEffectOffset, zStageMark, zStagePosition, stageMoveFields } from "./stageMovement";
import { zStoryItemCue } from "./storyItems";
import { zStoryPropCue } from "./storyProps";
import { zStoryTimerCue } from "./storyTimer";
import { zStageSound } from "./storyActions";
import { zBpm, zChoreo, zChoreoPlay, zGroove } from "./choreo";
const zPerformanceId = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-zA-Z0-9_-]+$/);
const text = z.string().max(16000);
const timing = {
  interaction: zStoryInteraction.optional(),
  delayStart: z.number().min(0).max(60000).optional(),
  duration: z.number().min(0).max(120000).optional(),
};
const forbiddenAdjacency = z.never().optional();

const cueText = z.string().min(1).max(16000);
const cueLevel = z.number().finite().min(0).max(1);
const cuePoint = z.object({ x: cueLevel, y: cueLevel }).passthrough();
const cueFacing = z.enum(["left", "right"]);
const cueMotion = z.enum(["giggle", "bounce", "nod", "headshake", "wiggle"]);
const cueTint = z.literal("memory-sepia").nullable();
const cueTarget = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("point"), x: cueLevel, y: cueLevel }).passthrough(),
  z.object({ kind: z.literal("puppet"), character: zPerformanceId, offset: zEffectOffset.optional() }).passthrough(),
  z.object({ kind: z.literal("prop"), character: zPerformanceId, offset: zEffectOffset.optional() }).passthrough(),
]);
const cueShape = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("circle"), center: cuePoint, radius: z.number().positive().max(1) }).passthrough(),
  z
    .object({
      kind: z.literal("rounded-rect"),
      x: cueLevel,
      y: cueLevel,
      width: z.number().positive().max(1),
      height: z.number().positive().max(1),
      cornerRadius: z.number().min(0).max(0.5).optional(),
    })
    .passthrough(),
]);
const cueLight = z.discriminatedUnion("effect", [
  z.object({
    effect: z.literal("spotlight-v2"),
    id: cueText,
    state: z.enum(["on", "off"]).optional(),
    target: cueTarget.optional(),
    radius: z.number().optional(),
    brightness: cueLevel.optional(),
    color: cueText.optional(),
  }),
  z.object({
    effect: z.literal("glow-v2"),
    id: cueText,
    state: z.enum(["on", "off"]).optional(),
    target: cueTarget.optional(),
    style: z.enum(["steady", "breathing", "rings"]).optional(),
    color: cueText.optional(),
    size: z.number().optional(),
  }),
  z.object({
    effect: z.literal("beam-v2"),
    id: cueText,
    state: z.enum(["on", "off"]).optional(),
    from: cueTarget.optional(),
    to: cueTarget.optional(),
    color: cueText.optional(),
  }),
  z.object({ effect: z.literal("blackout-v2"), keepSpotlights: z.boolean().optional() }),
  z.object({ effect: z.literal("restore-v2") }),
  z.object({ effect: z.literal("sweep"), durationMs: z.number().optional() }),
  z.object({ effect: z.literal("house"), level: cueLevel.nullable(), reset: z.boolean().optional() }).passthrough(),
  z.object({ effect: z.literal("location-light"), location: cueText, level: cueLevel.nullable() }).passthrough(),
  z
    .object({ effect: z.literal("character-light"), character: zPerformanceId, level: cueLevel.nullable() })
    .passthrough(),
  z.object({ effect: z.literal("ground"), color: cueText.nullable() }).passthrough(),
  z
    .object({
      effect: z.literal("followspots"),
      on: z
        .array(
          z
            .object({ character: zPerformanceId, brightness: cueLevel.optional(), color: cueText.optional() })
            .passthrough()
        )
        .nullable(),
    })
    .passthrough(),
  z.object({ effect: z.literal("dim"), level: cueLevel, sweep: z.boolean().optional() }).passthrough(),
  z.object({ effect: z.literal("brighten") }).passthrough(),
  z
    .object({
      effect: z.literal("wash"),
      preset: z.enum(["violet-vision", "alarm-red", "memory-sepia", "golden"]).nullable(),
      pulse: z.boolean().optional(),
    })
    .passthrough(),
  z.object({ effect: z.literal("blackout"), keepSpotlights: z.boolean().optional() }).passthrough(),
  z.object({ effect: z.literal("restore") }).passthrough(),
  z
    .object({
      effect: z.literal("spotlight"),
      target: cueTarget.nullable(),
      radius: z.number().positive().optional(),
      brightness: cueLevel.optional(),
    })
    .passthrough(),
  z
    .object({
      effect: z.literal("point-glow"),
      id: cueText,
      target: cueTarget.optional(),
      color: cueText.optional(),
      size: z.number().positive().optional(),
      pulse: z.boolean().optional(),
      off: z.boolean().optional(),
    })
    .passthrough(),
  z
    .object({
      effect: z.literal("pulse"),
      id: cueText,
      target: cueTarget.optional(),
      color: cueText.optional(),
      off: z.boolean().optional(),
    })
    .passthrough(),
  z
    .object({
      effect: z.literal("beam"),
      from: cueTarget.optional(),
      to: cueTarget.optional(),
      color: cueText.optional(),
      off: z.boolean().optional(),
    })
    .passthrough(),
  z.object({ effect: z.literal("silhouette"), character: zPerformanceId, off: z.boolean().optional() }).passthrough(),
  z.object({ effect: z.literal("tint"), character: zPerformanceId, preset: cueTint }).passthrough(),
  z
    .object({
      effect: z.literal("eyes"),
      state: z.enum(["open", "drift", "chase", "off"]),
      x: cueLevel.optional(),
      y: cueLevel.optional(),
      size: z.number().positive().optional(),
    })
    .passthrough(),
  z.object({ effect: z.literal("projection"), text: cueText.nullable() }).passthrough(),
  z.object({ effect: z.literal("flash"), durationMs: z.number().optional() }).passthrough(),
  z.object({ effect: z.literal("flicker"), durationMs: z.number().min(0).max(60000).optional() }).passthrough(),
  z
    .object({
      effect: z.literal("shake"),
      intensity: z.number().nonnegative().optional(),
      durationMs: z.number().nonnegative().optional(),
    })
    .passthrough(),
  z.object({ effect: z.literal("sound"), sound: zStageSound, volume: z.number().min(0).max(1).optional() }).passthrough(),
  z
    .object({
      effect: z.literal("sparkle"),
      variant: z.enum(["bubbles", "hearts", "glitter", "smoke", "clouds"]),
      emissionRate: z.number().finite().min(1).max(60).optional(),
      fadeMs: z.number().finite().min(50).max(10000).optional(),
      scale: z.number().finite().min(0.1).max(10).optional(),
      offset: zEffectOffset.optional(),
      anchor: z.enum(["body", "feet"]).optional(),
      from: cueTarget.optional(),
      to: cueTarget.optional(),
      via: z.array(cueTarget).max(24).optional(),
      durationMs: z.number().finite().min(0).max(60000).optional(),
      follow: zPerformanceId.optional(),
    })
    .passthrough(),
]);
const cuePlacement = {
  character: zPerformanceId,
  location: cueText.optional(),
  slot: z.number().min(1).max(5).multipleOf(0.5).optional(),
  mark: zStageMark.optional(),
  nudge: z.number().finite().optional(),
  scale: z.number().finite().positive().optional(),
  layer: z.number().finite().optional(),
  brightness: cueLevel.optional(),
  pose: cueText.optional(),
  orientation: cueFacing.optional(),
};
const cueFields = { actionKind: z.string().optional(), ...timing, start: forbiddenAdjacency, note: text.optional() };
/** All required cue fields are checked before authoring tools or the model can persist a beat. */
export const zStageRuntimeCue = z.discriminatedUnion("kind", [
  zStoryTimerCue.extend(cueFields).passthrough(),
  z.object({
    kind: z.literal("appearance"),
    character: zPerformanceId,
    persona: cueText.nullable().optional(),
    pose: cueText.optional(),
    orientation: cueFacing.optional(),
    tint: cueTint.optional(),
    silhouette: z.boolean().optional(),
    brightness: cueLevel.nullable().optional(),
    ...cueFields,
  }),
  z.object({
    kind: z.literal("set-backdrop"),
    backdropId: cueText.optional(),
    variant: cueText.nullable().optional(),
    surroundColor: cueText.nullable().optional(),
    ...cueFields,
  }),
  z.object({ kind: z.literal("title"), text: cueText, ...cueFields }).passthrough(),
  z
    .object({
      kind: z.literal("heading"),
      text: cueText,
      style: z.enum(["label", "transition"]).optional(),
      ...cueFields,
    })
    .passthrough(),
  z.object({ kind: z.literal("curtain"), state: z.enum(["open", "closed"]), ...cueFields }).passthrough(),
  z
    .object({
      kind: z.literal("layout"),
      mode: z.enum(["fullscreen", "stage", "compact", "standard", "performersLarge", "performersOnly"]).nullable(),
      ...cueFields,
    })
    .passthrough(),
  z
    .object({
      kind: z.literal("camera"),
      aspectRatio: zStoryAspectRatio.nullable().optional(),
      mode: z.enum(["auto", "manual"]).optional(),
      minVisible: z
        .union([z.number().min(1).max(100), z.enum(["phone", "tablet", "square", "landscape", "wide"])])
        .nullable()
        .optional(),
      focus: z
        .union([
          cueLevel,
          z.object({ character: zPerformanceId }),
          z.object({ characters: z.array(zPerformanceId).min(1) }),
        ])
        .nullable()
        .optional(),
      zoom: zCameraZoom.nullable().optional(),
      zoomCenter: zCameraZoomCenter.nullable().optional(),
      durationMs: z.number().min(0).max(60000).optional(),
      ...cueFields,
    })
    .passthrough(),
  z.object({ kind: z.literal("backdrop"), backdropId: cueText, ...cueFields }).passthrough(),
  z.object({ kind: z.literal("backdropVariant"), name: cueText.nullable(), ...cueFields }).passthrough(),
  z.object({ kind: z.literal("inScene"), ...cueFields }).passthrough(),
  z.object({ kind: z.literal("light"), cues: z.array(cueLight).max(100), ...cueFields }).passthrough(),
  z
    .object({
      kind: z.literal("emphasis"),
      id: cueText.optional(),
      shape: cueShape.optional(),
      color: cueText.optional(),
      traceMs: z.number().min(0).max(60000).optional(),
      off: z.literal(true).optional(),
      ...cueFields,
    })
    .passthrough(),
  z.object({ kind: z.literal("enter"), ...cuePlacement, manner: text.optional(), ...cueFields }).passthrough(),
  z.object({ kind: z.literal("move"), ...cuePlacement, ...stageMoveFields, ...cueFields }).passthrough(),
  z
    .object({
      kind: z.literal("exit"),
      characters: z.array(zPerformanceId).max(100),
      manner: text.optional(),
      ...cueFields,
    })
    .passthrough(),
  z
    .object({
      kind: z.literal("line"),
      text: cueText,
      character: zPerformanceId.optional(),
      speakerLabel: cueText.optional(),
      pose: cueText.optional(),
      orientation: cueFacing.optional(),
      hint: text.optional(),
      offstage: z.enum(["left", "top", "right", "bottom"]).optional(),
      offstageAt: cuePoint.partial().optional(),
      ...cueFields,
    })
    .passthrough(),
  z
    .object({
      kind: z.literal("narrator"),
      text: cueText,
      delivery: z.enum(["stage", "director"]).optional(),
      at: cuePoint.optional(),
      ...cueFields,
    })
    .passthrough(),
  z
    .object({
      kind: z.literal("reaction"),
      changes: z
        .array(
          z
            .object({
              character: zPerformanceId,
              pose: cueText.optional(),
              orientation: cueFacing.optional(),
              motion: cueMotion.optional(),
              groove: z.union([zGroove, z.literal("none")]).optional(),
              bpm: zBpm.optional(),
              choreo: z.union([cueText, zChoreo]).optional(),
              play: zChoreoPlay.optional(),
            })
            .passthrough()
        )
        .max(100),
      ...cueFields,
    })
    .passthrough(),
  z
    .object({
      kind: z.literal("sfx"),
      sound: cueText,
      tone: z.enum(["whisper", "quiet", "normal", "loud", "explosive"]).optional(),
      character: zPerformanceId.optional(),
      at: cuePoint.optional(),
      description: text.optional(),
      ...cueFields,
    })
    .passthrough(),
  z.object({ kind: z.literal("beat"), ...cueFields }).passthrough(),
  z
    .object({
      kind: z.literal("roles"),
      tints: z.array(z.object({ character: zPerformanceId, preset: cueTint }).passthrough()).optional(),
      personas: z.array(z.object({ character: zPerformanceId, persona: cueText.nullable() }).passthrough()).optional(),
      ...cueFields,
    })
    .passthrough(),
  zStoryItemCue.extend(cueFields).passthrough(),
  zStoryPropCue.extend(cueFields).passthrough(),
  z.object({ kind: z.literal("end"), ...cueFields }).passthrough(),
]);
